import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ensureKey, clearKey } from "@/lib/offline/crypto";
import { startAutoSync, syncAll } from "@/lib/offline/sync-engine";
import { refreshPendingCount } from "@/lib/offline/outbox";
import { wipeOfflineDB } from "@/lib/offline/db";

/**
 * Boots the offline subsystem once on mount:
 *  - Derives an encryption key from the current session.
 *  - Starts the auto-sync loop (reconnect listener + 60s heartbeat).
 *  - Reacts to auth state changes (key rotation / logout wipe).
 *
 * Render once near the top of the tree (in __root.tsx).
 */
export function OfflineProvider() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token ?? null;
      if (!cancelled) await ensureKey(token);
      await refreshPendingCount();
      startAutoSync();
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        clearKey();
        await wipeOfflineDB();
        return;
      }
      await ensureKey(session?.access_token ?? null);
      // Best-effort sync after sign-in / token refresh.
      void syncAll();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return null;
}
